import type { ShippingMethod, CheckProductShippabilityInput } from '@/lib/types/shipping';
import type { ProcessedShippingEligibility, AvailableShippingMethod } from '@/lib/types/shippingEligibilityState';
import { DEFAULT_COUNTRY_CODE } from '@/lib/constants/countries';
import { isShippingMethodEligible } from '@/lib/functions/shippingRules';

const CACHE_VERSION = '2.0';

export const isLocationEligibleForMethod = (
  method: ShippingMethod,
  locationInput: CheckProductShippabilityInput,
): boolean => isShippingMethodEligible(method, locationInput);

export const processShippingMethodsForLocation = (
  methods: ShippingMethod[],
  state: string | null,
  zipCode: string | null,
  countryCode: string | null = DEFAULT_COUNTRY_CODE,
): ProcessedShippingEligibility => {
  const availableMethods: AvailableShippingMethod[] = [];

  const locationInput: CheckProductShippabilityInput = {
    productType: '',
    classId: '',
    category: null,
    provinceCode: state,
    postalCode: zipCode,
    countryCode,
  };

  methods.forEach((method) => {
    const isAvailable = isLocationEligibleForMethod(method, locationInput);

    if (!isAvailable) {
      return;
    }

    const excludedTypes: string[] = [];
    const excludedClassCodes: string[] = [];

    method.rules.forEach((rule) => {
      if (rule.type === 'contains_types' && rule.configuration.action === 'deny') {
        const types = (rule.configuration.types || '').split(',').map((t: string) => t.trim()).filter(Boolean);
        excludedTypes.push(...types);
      } else if (rule.type === 'contains_class_codes' && rule.configuration.action === 'deny') {
        const codes = (rule.configuration.codes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
        excludedClassCodes.push(...codes);
      } else if (rule.type === 'restricted_product_zip') {
        const restrictedTypes = (rule.configuration.types || '').split(',').map((t: string) => t.trim()).filter(Boolean);
        const restrictedClassCodes = (rule.configuration.class_codes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
        const allowedZips = (rule.configuration.allowed_zips || '').split(',').map((z: string) => z.trim()).filter(Boolean);
        const isZipAllowed = zipCode !== null && allowedZips.includes(zipCode);

        if (!isZipAllowed) {
          excludedTypes.push(...restrictedTypes);
          excludedClassCodes.push(...restrictedClassCodes);
        }
      } else if (rule.type === 'restricted_product_state') {
        const restrictedTypes = (rule.configuration.types || '').split(',').map((t: string) => t.trim()).filter(Boolean);
        const restrictedClassCodes = (rule.configuration.class_codes || '').split(',').map((c: string) => c.trim()).filter(Boolean);
        const allowedStates = (rule.configuration.allowed_states || '').split(',').map((s: string) => s.trim()).filter(Boolean);
        const isStateAllowed = state !== null && allowedStates.includes(state);

        if (!isStateAllowed) {
          excludedTypes.push(...restrictedTypes);
          excludedClassCodes.push(...restrictedClassCodes);
        }
      }
    });

    availableMethods.push({
      code: method.code,
      name: method.name,
      excludedTypes,
      excludedClassCodes,
    });
  });

  const universallyExcludedTypes: string[] = [];
  const universallyExcludedClassCodes: string[] = [];

  if (availableMethods.length > 0) {
    const allTypes = new Set<string>();
    const allCodes = new Set<string>();

    availableMethods.forEach((method) => {
      method.excludedTypes.forEach((type) => allTypes.add(type));
      method.excludedClassCodes.forEach((code) => allCodes.add(code));
    });

    allTypes.forEach((type) => {
      if (availableMethods.every((method) => method.excludedTypes.includes(type))) {
        universallyExcludedTypes.push(type);
      }
    });

    allCodes.forEach((code) => {
      if (availableMethods.every((method) => method.excludedClassCodes.includes(code))) {
        universallyExcludedClassCodes.push(code);
      }
    });
  }

  return {
    availableMethods,
    excludedTypes: universallyExcludedTypes,
    excludedClassCodes: universallyExcludedClassCodes,
    excludedStates: [],
    state,
    zipCode,
    hasBeenProcessed: true,
    timestamp: Date.now(),
    version: CACHE_VERSION,
  };
};
